import { $api } from '../y/api';
import { $time } from '../y/time';
import { $id, YId, YTimeString } from '../y/types';
import { CBookingId, cBookingIsPastForSession, CBookingPartId, CBookingPartStatus, CRating } from './c-booking';
import { CTicketType } from './c-producer';
import { c_venues, CVenueSceneView } from './c-venue';
import { EMaybePaymentIntent } from './e-payment-intent';
import { TTicketHomeView } from './t-ticket';
import { legacy_events, LegacyAvailability, LegacyEventModel } from './zzz-legacy-open-events';

export interface CBookingHomeView {
    id: CBookingId;
    part_id: CBookingPartId;
    is_test: boolean;
    session_id: YId;
    session_time: YTimeString;
    sales_end_at: YTimeString;
    event_id: YId;
    token: string;
    number: string;
    quantity: number;
    base_quantity: number;
    status: CBookingPartStatus;
    rating?: CRating;
    expires_at?: YTimeString;
    has_purchases: boolean;
}

export function cBookingsBook(session_id: YId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CBookingHomeView>>('c/bookings/book', { session_id, quantity });
}

export function cBookingsStandby(session_id: YId, quantity: number, booking_id?: CBookingId) {
    return $api.POST<CBookingHomeView>('c/bookings/standby', { session_id, quantity, booking_id });
}

export function cBookingBuyMore(booking_id: CBookingId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CBookingHomeView>>(`c/booking/${booking_id}/buy_more`, { quantity });
}

interface BookingInfoResponse {
    booking: CBookingHomeView;
    availability: LegacyAvailability;
    event: LegacyEventModel;
    venue: CVenueSceneView;
    ticket_type: CTicketType;
    tickets?: TTicketHomeView[];
}

export function cBookingPartInfoView(id: CBookingPartId) {
    return $api.GET<BookingInfoResponse>(`c/booking_part/${id}/info_view`).then(r => {
        c_venues.register(r.venue);
        return [r.booking, legacy_events.register(r.event), r.venue, r.availability, r.ticket_type, r.tickets || []] as const;
    });
}

interface UnbookInfoResponse {
    is_late: boolean;
}

export function cBookingPartGetUnbookInfo(id: CBookingPartId) {
    return $api.GET<UnbookInfoResponse>(`c/booking_part/${id}/unbook`)
}

interface BookingsHomeViewResponse {
    bookings: CBookingHomeView[];
    events: LegacyEventModel[];
    venues: CVenueSceneView[];
}

let bookings_home_view_promise: Promise<CBookingHomeView[]> | undefined;
export function cBookingsHomeView(reload?: boolean) {
    if(!bookings_home_view_promise || reload) {
        bookings_home_view_promise = $api.GET<BookingsHomeViewResponse>(`c/bookings/home_view`).then(response => {
            response.venues.forEach(c_venues.register);
            response.events.forEach(legacy_events.register);
            return response.bookings;
        });
    }

    return bookings_home_view_promise;
}

export function cBookingsFilterStandby(b: CBookingHomeView) {
    const session = legacy_events.maybe($id.idToObjectId(b.event_id))?.getSession($id.idToObjectId(b.session_id))!;
    if(!session) return false;
    return (b.status == 'standby' || b.status == 'standby_reserved') && !$time(b.sales_end_at).is_past;
}

export function cBookingsSortStandby(a: CBookingHomeView, b: CBookingHomeView) {
    if(a.status == 'standby_reserved') {
        if(b.status != 'standby_reserved')
            return -1;
    } else if(b.status == 'standby_reserved') {
        return 1;
    }

    return $time.compare($time(a.session_time), $time(b.session_time));
}

export function cBookingsFilterActive(b: CBookingHomeView) {
    const session = legacy_events.maybe($id.idToObjectId(b.event_id))?.getSession($id.idToObjectId(b.session_id))!;
    if(!session) return false;
    return b.status == 'confirmed' && !cBookingIsPastForSession(session);
}

export function cBookingsSortActive(a: CBookingHomeView, b: CBookingHomeView) {
    return $time.compare($time(a.session_time), $time(b.session_time));
}

export function cBookingsFilterPast(b: CBookingHomeView) {
    const session = legacy_events.maybe($id.idToObjectId(b.event_id))?.getSession($id.idToObjectId(b.session_id))!;
    if(!session) return false;
    return b.status == 'confirmed' && cBookingIsPastForSession(session);
}

export function cBookingsSortPast(a: CBookingHomeView, b: CBookingHomeView) {
    return $time.compare($time(b.session_time), $time(a.session_time));
}
